"use client";

import { UserPlus } from "lucide-react";
import { type CSSProperties, type ReactNode, useId } from "react";
import { cn } from "@/lib/utils";
import {
  PreviewAvatarGroup,
  PreviewMetadataLabel,
  type PreviewParticipant,
} from "./reference-preview-primitives";

/**
 * Shared stage for the racket / court scenes (tennis, badminton, table
 * tennis, padel, pickleball, squash).
 *
 * The scenes lead every session card at 301×220 and still render in activity
 * posts at ~350–600px wide, so sizing uses CONTAINER queries (`@container` on
 * the frame, `@sm` = 384px) — never viewport `sm:`, which is true in a 640px
 * gallery and false on a phone.
 *
 * Artwork is one static SVG per court drawn in a 300×220 box with
 * `preserveAspectRatio="none"`: the court is a perspective trapezoid, so a
 * horizontal stretch at wider sizes keeps every line straight. Strokes use
 * `vector-effect: non-scaling-stroke`, and anything round (balls, caps) is
 * HTML so it never turns into an ellipse.
 *
 * Layout contract:
 * - top-center: the activity label pill;
 * - left / right: each side's players as a vertical, net-ward staggered stack
 *   (singles 1, doubles 2, teams 3 faces + "+N") — a column never collides
 *   with the score, at any width;
 * - centre: the score (FULL) in normal flow with its set/game chips.
 */

export type RacketMatchState = "EMPTY" | "NO_SCORE" | "FULL";

export function getRacketMatchState(
  yourTeam: unknown[],
  opponentTeam: unknown[],
  hasScore: boolean,
): RacketMatchState {
  if (yourTeam.length === 0 && opponentTeam.length === 0) return "EMPTY";
  return hasScore ? "FULL" : "NO_SCORE";
}

// ─── Perspective court geometry ──────────────────────────────────────────────

export type CourtProjection = {
  /** Screen y of the far edge (v = 0) and near edge (v = 1). */
  yFar: number;
  yNear: number;
  /** Half-widths of the far and near edges, around x = 150. */
  farHalfWidth: number;
  nearHalfWidth: number;
};

export type Projector = (u: number, v: number) => [number, number];

/**
 * Perspective-correct mapping from court coordinates — `u` along the length
 * (0 = left baseline, 1 = right baseline), `v` across it (0 = far sideline,
 * 1 = near sideline) — onto a ground plane seen from an elevated side camera.
 * Screen y and width are both linear in 1/z, so lines nearer the camera
 * spread out the way a real broadcast view does.
 */
export function createCourtProjector({
  yFar,
  yNear,
  farHalfWidth,
  nearHalfWidth,
}: CourtProjection): Projector {
  const ratio = nearHalfWidth / farHalfWidth;
  const sFar = 1 / ratio;
  return (u, v) => {
    const s = 1 / (ratio + (1 - ratio) * v);
    const t = (s - sFar) / (1 - sFar);
    const halfWidth = nearHalfWidth * s;
    return [150 + (u - 0.5) * 2 * halfWidth, yFar + (yNear - yFar) * t];
  };
}

const fmt = ([x, y]: [number, number]) => `${x.toFixed(1)},${y.toFixed(1)}`;

/** Closed path for the court rectangle [u0,u1]×[v0,v1]. */
export function courtQuad(
  p: Projector,
  u0: number,
  v0: number,
  u1: number,
  v1: number,
) {
  return `M${fmt(p(u0, v0))}L${fmt(p(u1, v0))}L${fmt(p(u1, v1))}L${fmt(p(u0, v1))}Z`;
}

/** Open path for one or more court segments [[u0,v0,u1,v1], …]. */
export function courtLines(
  p: Projector,
  segments: Array<[number, number, number, number]>,
) {
  return segments
    .map(([u0, v0, u1, v1]) => `M${fmt(p(u0, v0))}L${fmt(p(u1, v1))}`)
    .join("");
}

/** Alternating stripe bands along the length (mowing / panel stripes). */
export function courtStripes(
  p: Projector,
  count: number,
  from: number,
  to: number,
  v0: number,
  v1: number,
) {
  const step = (to - from) / count;
  let d = "";
  for (let i = 0; i < count; i += 2) {
    d += courtQuad(p, from + i * step, v0, from + (i + 1) * step, v1);
  }
  return d;
}

/** Unique, url()-safe id prefix for one SVG's gradients and patterns. */
export function useSvgIdPrefix() {
  return `rc${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

/** Full-bleed SVG layer in the 300×220 artwork box. */
export function CourtSvg({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      className={cn("absolute inset-0 h-full w-full", className)}
    >
      {children}
    </svg>
  );
}

/** Props for a crisp painted line at any stretch. */
export const PAINT_LINE = {
  fill: "none",
  vectorEffect: "non-scaling-stroke",
  strokeLinecap: "square",
  strokeLinejoin: "miter",
} as const;

// ─── Props on the court (HTML so they stay round) ────────────────────────────

/** A soft contact shadow under a prop. */
export function PropShadow({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={cn(
        "absolute rounded-[50%] bg-[radial-gradient(closest-side,rgba(0,0,0,0.55),transparent)]",
        className,
      )}
      style={style}
    />
  );
}

// ─── Frame ───────────────────────────────────────────────────────────────────

type RacketMatchFrameProps = {
  label: string;
  frameClassName: string;
  matchState: RacketMatchState;
  /** Static artwork (absolutely positioned layers). */
  scene: ReactNode;
  /** Props over the artwork, under the UI. */
  decor?: ReactNode;
  yourTeam: PreviewParticipant[];
  opponentTeam: PreviewParticipant[];
  yourWinner?: boolean;
  opponentWinner?: boolean;
  linkToProfiles?: boolean;
  /** Score block for the FULL state. */
  center?: ReactNode;
};

export function RacketMatchFrame({
  label,
  frameClassName,
  matchState,
  scene,
  decor,
  yourTeam,
  opponentTeam,
  yourWinner,
  opponentWinner,
  linkToProfiles = false,
  center,
}: RacketMatchFrameProps) {
  return (
    <div
      className={cn(
        "group @container relative isolate h-[220px] w-full overflow-hidden rounded-3xl border border-black/40 shadow-[0_8px_30px_rgba(0,0,0,0.6)] select-none",
        frameClassName,
      )}
    >
      <div className="pointer-events-none absolute inset-0 z-0">{scene}</div>
      {decor ? (
        <div className="pointer-events-none absolute inset-0 z-[5]">
          {decor}
        </div>
      ) : null}

      <PreviewMetadataLabel label={label} />

      {matchState === "EMPTY" ? <RacketWaiting /> : null}

      {/* The play band starts under the label pill. */}
      <div className="pointer-events-none absolute inset-x-0 top-[34px] bottom-1.5 z-20">
        <div className="absolute inset-y-0 left-3 flex items-center @sm:left-6">
          <RacketTeamAnchor
            team={yourTeam}
            side="left"
            isWinner={!!yourWinner}
            isLoser={!!opponentWinner}
            linkToProfiles={linkToProfiles}
          />
        </div>
        {center ? (
          <div className="absolute inset-y-0 right-[64px] left-[64px] flex flex-col items-center justify-center @sm:right-[96px] @sm:left-[96px]">
            {center}
          </div>
        ) : null}
        <div className="absolute inset-y-0 right-3 flex items-center @sm:right-6">
          <RacketTeamAnchor
            team={opponentTeam}
            side="right"
            isWinner={!!opponentWinner}
            isLoser={!!yourWinner}
            linkToProfiles={linkToProfiles}
          />
        </div>
      </div>
    </div>
  );
}

function RacketWaiting() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
      <span className="flex items-center gap-3 rounded-full bg-black/30 px-3 py-1 text-[10px] font-semibold tracking-[0.4em] text-white/75 uppercase ring-1 ring-white/10">
        Waiting
      </span>
    </div>
  );
}

// ─── Team anchor ─────────────────────────────────────────────────────────────

/** Avatar size by how many chips the column holds. */
const AVATAR_SIZE: Record<number, string> = {
  1: "h-12 w-12 @sm:h-14 @sm:w-14",
  2: "h-11 w-11 @sm:h-12 @sm:w-12",
  3: "h-10 w-10 @sm:h-11 @sm:w-11",
  4: "h-9 w-9 @sm:h-10 @sm:w-10",
};

/** Doubles: two faces. Teams: three faces + "+N". */
const MAX_FACES = 3;

function RacketTeamAnchor({
  team,
  side,
  isWinner,
  isLoser,
  linkToProfiles,
}: {
  team: PreviewParticipant[];
  side: "left" | "right";
  isWinner: boolean;
  isLoser: boolean;
  linkToProfiles: boolean;
}) {
  if (team.length === 0) {
    return (
      <div className="pointer-events-none flex h-12 w-12 items-center justify-center rounded-[30%] border border-dashed border-white/45 bg-black/20 @sm:h-14 @sm:w-14">
        <UserPlus className="h-4 w-4 text-white/70" />
      </div>
    );
  }

  const chips = Math.min(team.length, MAX_FACES + 1);
  const size = AVATAR_SIZE[chips] ?? AVATAR_SIZE[4];

  return (
    <div className="pointer-events-auto relative">
      {isWinner ? (
        <div className="pointer-events-none absolute -inset-4 rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.32),rgba(255,255,255,0.08)_60%,transparent)]" />
      ) : null}
      <PreviewAvatarGroup
        participants={team}
        maxVisible={MAX_FACES}
        linkToProfiles={linkToProfiles}
        emphasize={isWinner}
        // A column, each other player stepping toward the net — reads as a
        // doubles formation and never widens into the score.
        groupClassName={cn(
          "flex-col -space-y-2 space-x-0 sm:space-x-0",
          side === "left"
            ? "[&>*:nth-child(even)]:translate-x-2.5"
            : "[&>*:nth-child(even)]:-translate-x-2.5",
        )}
        avatarClassName={cn(
          size,
          "shadow-[0_6px_14px_rgba(0,0,0,0.45)] ring-2",
          isWinner ? "ring-white/50" : "ring-black/30",
          isLoser && "opacity-90 saturate-[0.85]",
        )}
        overflowClassName={cn(
          size,
          "bg-black/55 shadow-[0_6px_14px_rgba(0,0,0,0.45)] backdrop-blur-none",
        )}
      />
    </div>
  );
}

// ─── Score ───────────────────────────────────────────────────────────────────

type RacketScoreProps = {
  left: number;
  right: number;
  leftWinner?: boolean;
  rightWinner?: boolean;
  /** Per-set / per-game scores, left side first. */
  breakdown?: Array<{ yourScore: number; opponentScore: number }>;
  /** Classes for the delimiter bar (sport accent colour + glow). */
  accentClassName: string;
};

export function RacketScore({
  left,
  right,
  leftWinner,
  rightWinner,
  breakdown,
  accentClassName,
}: RacketScoreProps) {
  const numeral =
    "font-black text-[54px] leading-none tracking-tighter tabular-nums [text-shadow:0_4px_18px_rgba(0,0,0,0.55),0_1px_2px_rgba(0,0,0,0.5)] @sm:text-[80px]";
  return (
    <div className="relative flex flex-col items-center gap-1.5 @sm:gap-2.5">
      {/* A soft pool of shade so a dimmed numeral still reads over bright
          art (the squash front wall, a lit table). */}
      <div className="pointer-events-none absolute -inset-x-8 -inset-y-5 -z-10 bg-[radial-gradient(closest-side,rgba(0,0,0,0.38),rgba(0,0,0,0.16)_60%,transparent)]" />
      <div className="flex items-center gap-2.5 @sm:gap-4">
        <span
          className={cn(
            numeral,
            leftWinner || !rightWinner ? "text-white" : "text-white/70",
          )}
        >
          {left}
        </span>
        <span
          className={cn(
            "h-[6px] w-4 shrink-0 rounded-full @sm:h-2 @sm:w-6",
            accentClassName,
          )}
        />
        <span
          className={cn(
            numeral,
            rightWinner || !leftWinner ? "text-white" : "text-white/70",
          )}
        >
          {right}
        </span>
      </div>
      {breakdown && breakdown.length > 0 ? (
        <div className="flex max-w-[168px] flex-wrap justify-center gap-1 @sm:max-w-[320px] @sm:gap-1.5">
          {breakdown.map((part, index) => {
            const leftTook = part.yourScore > part.opponentScore;
            return (
              <span
                // Scores repeat, so position is the identity.
                // biome-ignore lint/suspicious/noArrayIndexKey: ordered sets
                key={index}
                className="rounded-full bg-black/45 px-1.5 py-[3px] text-[10px] leading-none font-bold text-white/70 tabular-nums ring-1 ring-white/12 @sm:px-2 @sm:text-[12px]"
              >
                <span className={leftTook ? "text-white" : undefined}>
                  {part.yourScore}
                </span>
                <span className="text-white/40">-</span>
                <span className={leftTook ? undefined : "text-white"}>
                  {part.opponentScore}
                </span>
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export type { PreviewParticipant };
